import { z } from 'zod';

import { idSchema, isoDateTimeSchema, paginated, paginationQuerySchema } from '../common';
import { agentStatusSchema, platformSchema, userRoleSchema } from '../enums';
import { browserProfileSchema } from './browser';
import { queryBooleanSchema } from './posts';

/**
 * `/agents` DTOs — pairing, inventory, revocation and token rotation.
 *
 * Pairing is the one moment a shared secret exists in plaintext. Everything
 * else about an agent is a read model: the server stores hashes, statuses and
 * version numbers, never a platform credential and never a browser profile.
 */

export const deviceNameSchema = z.string().trim().min(1).max(120);

// ---------------------------------------------------------------------------
// POST /agents/pairing-key
// ---------------------------------------------------------------------------

export const createPairingKeyRequestSchema = z.object({
  label: z.string().trim().max(120).optional(),
  /** Short by design — the key is typed into the desktop app immediately. */
  ttlMinutes: z.number().int().min(1).max(60).default(15),
});
export type CreatePairingKeyRequest = z.infer<typeof createPairingKeyRequestSchema>;

/**
 * The ONLY response in the entire API that contains a plaintext pairing key.
 *
 * The server stores nothing but a hash of it, so this value can never be read
 * back: `GET /agents/pairing-keys` returns metadata only, and losing the value
 * means issuing a new key. It is single-use and expires at `expiresAt`. Do not
 * log it, do not persist it in the dashboard, and show it once.
 */
export const pairingKeyResponseSchema = z.object({
  id: idSchema,
  pairingKey: z.string().min(8).max(128),
  label: z.string().nullable(),
  expiresAt: isoDateTimeSchema,
  createdAt: isoDateTimeSchema,
});
export type PairingKeyResponse = z.infer<typeof pairingKeyResponseSchema>;

/** Metadata-only view; the key itself is unrecoverable after issuance. */
export const pairingKeySummarySchema = z.object({
  id: idSchema,
  label: z.string().nullable(),
  expiresAt: isoDateTimeSchema,
  createdAt: isoDateTimeSchema,
  usedAt: isoDateTimeSchema.nullable(),
  usedByAgentId: idSchema.nullable(),
});
export type PairingKeySummary = z.infer<typeof pairingKeySummarySchema>;

export const pairingKeyListResponseSchema = z.object({
  items: z.array(pairingKeySummarySchema),
});
export type PairingKeyListResponse = z.infer<typeof pairingKeyListResponseSchema>;

// ---------------------------------------------------------------------------
// POST /agents/pair  (called by the desktop agent, unauthenticated)
// ---------------------------------------------------------------------------

export const pairAgentRequestSchema = z.object({
  pairingKey: z.string().min(8).max(128),
  deviceName: deviceNameSchema,
  osVersion: z.string().trim().min(1).max(120),
  appVersion: z.string().trim().min(1).max(40),
  /**
   * Which adapter generation this build ships. The server refuses to pair — or
   * pushes `agent.update_required` — when it is older than the minimum an
   * assigned SelectorPack demands.
   */
  adapterVersion: z.number().int().min(1),
});
export type PairAgentRequest = z.infer<typeof pairAgentRequestSchema>;

/**
 * `deviceToken` is the agent's long-lived credential for the WebSocket. Like
 * the pairing key it is returned exactly once and stored server-side only as a
 * hash; the agent keeps it in Electron `safeStorage`, never in a log file.
 */
export const pairAgentResponseSchema = z.object({
  agentId: idSchema,
  deviceToken: z.string().min(32).max(512),
  /**
   * Role of the user who ISSUED the pairing key, at the moment of pairing. The
   * desktop app derives its local feature set from this: MEMBER hides and
   * refuses the calibration tooling (Dump DOM, interactive browser). Optional
   * so a new agent still pairs against an older server; absent means an older
   * server, which never restricted anything — the agent treats that as ADMIN.
   * Changing a user's role does not reach an already-paired agent: re-pair.
   */
  operatorRole: userRoleSchema.optional(),
  /** Absolute wss:// URL, built from env — never hardcoded in the agent. */
  websocketUrl: z.string().url(),
  workspaceId: idSchema,
  heartbeatIntervalSeconds: z.number().int().min(5).max(300),
  pairedAt: isoDateTimeSchema,
});
export type PairAgentResponse = z.infer<typeof pairAgentResponseSchema>;

// ---------------------------------------------------------------------------
// GET /agents
// ---------------------------------------------------------------------------

export const agentSummarySchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  deviceName: z.string(),
  status: agentStatusSchema,
  osVersion: z.string(),
  appVersion: z.string(),
  adapterVersion: z.number().int().min(1),
  /** Cached pack version per platform; a missing key means the agent still runs
   *  its bundled baseline for that channel. */
  selectorPackVersions: z.record(platformSchema, z.number().int().min(0)),
  lastHeartbeatAt: isoDateTimeSchema.nullable(),
  lastIpAddress: z.string().nullable(),
  browserProfileCount: z.number().int().min(0),
  activeProjectCount: z.number().int().min(0),
  updateRequired: z.boolean(),
  pairedAt: isoDateTimeSchema,
  revokedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type AgentSummary = z.infer<typeof agentSummarySchema>;

export const agentListQuerySchema = paginationQuerySchema.extend({
  status: z.union([agentStatusSchema, z.array(agentStatusSchema)]).optional(),
  search: z.string().trim().max(120).optional(),
  includeRevoked: queryBooleanSchema.default(false),
});
export type AgentListQuery = z.infer<typeof agentListQuerySchema>;

export const agentListResponseSchema = paginated(agentSummarySchema);
export type AgentListResponse = z.infer<typeof agentListResponseSchema>;

// ---------------------------------------------------------------------------
// GET /agents/:id
// ---------------------------------------------------------------------------

export const agentDetailResponseSchema = z.object({
  agent: agentSummarySchema,
  /** The dashboard shows agent-online, platform-logged-in and project-running
   *  as three separate signals; the profiles carry the latter two. */
  browserProfiles: z.array(browserProfileSchema),
  activeProjectIds: z.array(idSchema),
  lastEventAt: isoDateTimeSchema.nullable(),
  lastErrorMessage: z.string().nullable(),
});
export type AgentDetailResponse = z.infer<typeof agentDetailResponseSchema>;

export const updateAgentRequestSchema = z.object({
  deviceName: deviceNameSchema,
});
export type UpdateAgentRequest = z.infer<typeof updateAgentRequestSchema>;

// ---------------------------------------------------------------------------
// POST /agents/:id/revoke
// ---------------------------------------------------------------------------

/**
 * A device releasing ITSELF — `POST /agents/unpair`.
 *
 * Unpairing used to be a local-only act: the agent wiped its own credentials and
 * the server kept counting the seat, so the next pairing attempt hit the plan
 * limit and the operator was told to "revoke an agent" from the dashboard. That
 * detour ends in a NEW agent id, and until the Chromium directory was decoupled
 * from that id it also meant signing back into Facebook by hand. Letting the
 * device hand the seat back closes the loop.
 *
 * The device token is the credential, exactly as it is on the WebSocket
 * handshake. Anyone holding it can already act as the agent, so being able to
 * retire it grants nothing new — and the operator can always pair again.
 */
export const unpairAgentRequestSchema = z.object({
  agentId: idSchema,
  deviceToken: z.string().min(32).max(512),
  reason: z.string().trim().max(500).optional(),
});
export type UnpairAgentRequest = z.infer<typeof unpairAgentRequestSchema>;

export const unpairAgentResponseSchema = z.object({
  agentId: idSchema,
  releasedAt: isoDateTimeSchema,
});
export type UnpairAgentResponse = z.infer<typeof unpairAgentResponseSchema>;

export const revokeAgentRequestSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});
export type RevokeAgentRequest = z.infer<typeof revokeAgentRequestSchema>;

/**
 * Revocation invalidates the device token immediately and drops the socket.
 * Projects bound to the agent's profiles are stopped; nothing is deleted from
 * the user's machine, because the server has no such reach by design.
 */
export const revokeAgentResponseSchema = z.object({
  agentId: idSchema,
  status: agentStatusSchema,
  revokedAt: isoDateTimeSchema,
  stoppedProjectIds: z.array(idSchema),
});
export type RevokeAgentResponse = z.infer<typeof revokeAgentResponseSchema>;

// ---------------------------------------------------------------------------
// POST /agents/:id/rotate-token
// ---------------------------------------------------------------------------

/** Like pairing, the new token is shown once and stored only as a hash. */
export const rotateAgentTokenResponseSchema = z.object({
  agentId: idSchema,
  deviceToken: z.string().min(32).max(512),
  websocketUrl: z.string().url(),
  rotatedAt: isoDateTimeSchema,
  /** Grace window in which the previous token still authenticates, so a
   *  disconnected agent can finish reconnecting instead of falling off. */
  previousTokenValidUntil: isoDateTimeSchema.nullable(),
});
export type RotateAgentTokenResponse = z.infer<typeof rotateAgentTokenResponseSchema>;

// ---------------------------------------------------------------------------
// GET /agents/installer/info  ·  GET /agents/installer
// ---------------------------------------------------------------------------

/**
 * Whether the server has an agent installer to hand out, and what it is.
 *
 * `available: false` carries a `reason` instead of being a bare 404: the
 * download button lives on a screen every role sees, and "the server has no
 * installer configured" is an answer the operator can act on, while a dead
 * button is not. The file itself is streamed by `GET /agents/installer`.
 */
export const agentInstallerInfoResponseSchema = z.object({
  available: z.boolean(),
  fileName: z.string().nullable(),
  sizeBytes: z.number().int().min(0).nullable(),
  /** Vietnamese, shown verbatim under the disabled button. Null when available. */
  reason: z.string().nullable(),
});
export type AgentInstallerInfoResponse = z.infer<typeof agentInstallerInfoResponseSchema>;
